/* eslint-disable @typescript-eslint/no-explicit-any */
import { RGBAColor } from '@deck.gl/core';
import { SolidPolygonLayer } from '@deck.gl/layers';
import { Dispatch } from 'redux';
import { GeoJSON, PropLocationItem, VarName } from '../../../services/api';
import { varNameBandParams } from '../../../utils/dataBandParams';
import {
  FloorHoverType,
  hexToRgb,
  HoveredFloor,
  weightedColorWithBands,
  weightedColorWithoutBands,
} from '../mapHelpers';
import { themeProps } from '../../../styles/theme';
import { LocSensorStatusTree } from '../../../state/types';
import { ActionTypes } from '../../../state/actionTypes';
import { setCurrentLocation } from '../../../state/actions';

const defaultElevation = 7;
const defaultOpacity = 50;

//  Helper function to process a single feature with elevated data
function processFeature(feature: any, elevationPerLevel: number): any {
  // Only process features of type "area.floor"
  if (feature?.properties?.type === 'area.floor' && feature?.geometry?.type === 'MultiPolygon') {
    const elevation = feature.properties.level * elevationPerLevel;
    if (typeof elevation !== 'number' || Number.isNaN(elevation)) {
      // Decide on fallback behavior: return feature unchanged? Use default elevation?
      return feature;
    }

    // Create a new feature object to avoid modifying the parameter
    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: feature.geometry.coordinates.map((polygon: any[]) =>
          polygon.map((ring: any[]) =>
            ring.map((point: number[]) => {
              // If the point has only x,y coordinates
              if (point.length === 2) {
                // Add the z-coordinate (elevation)
                return [...point, elevation];
              }
              // If it already has a z-coordinate, update it
              const newPoint = [...point];
              newPoint[2] = elevation;
              return newPoint;
            })
          )
        ),
      },
    };
  }
  // Return the feature unchanged if it's not a floor area
  return feature;
}

// Adds elevation to GeoJSON features of type "area.floor" by adding a z-coordinate to each point in the geometry.
function addElevationToFloors(geojson: GeoJSON, elevationPerLevel: number): GeoJSON {
  // Create a deep copy to avoid modifying the original data
  const result = JSON.parse(JSON.stringify(geojson));
  // Process a feature collection
  if (result.type === 'FeatureCollection' && Array.isArray(result.features)) {
    result.features = result.features.map((feature: any) =>
      processFeature(feature, elevationPerLevel)
    );
  }
  // Process a single feature
  else if (result.type === 'Feature') {
    return processFeature(result, elevationPerLevel);
  }
  return result;
}

const get3DBuildingLayer = (
  currentLocation: string,
  childLocations: PropLocationItem[],
  activeMarker: VarName,
  locSensorStatusData: LocSensorStatusTree,
  floorPlan: GeoJSON | undefined,
  motionThreshold: [number, number],
  hoveredFloor?: HoveredFloor,
  setHoveredFloor?: (value: HoveredFloor | undefined) => void,
  dispatch?: Dispatch<ActionTypes>
) => {
  if (!floorPlan) return null;
  const onlineVarData = locSensorStatusData.onlineData.get(activeMarker) ?? [];
  const hoveredFloorId = hoveredFloor?.floorId;

  const getFillColor = (item: any) => {
    const locName = item.properties?.name;
    if (!locName) {
      return hexToRgb(themeProps.colors.mediumGrey, 255);
    }

    const isHoveredFloor = locName === hoveredFloorId;
    let opacity = 255;
    if (hoveredFloorId && !isHoveredFloor) opacity = defaultOpacity;

    const locId = `${currentLocation}#${item.properties.name}`;
    // use band if avaiable, if not generate min/max value and calculate color weight based on total
    const fillColor = varNameBandParams[activeMarker]
      ? weightedColorWithBands(activeMarker, onlineVarData, locId, opacity, motionThreshold)
      : weightedColorWithoutBands(locId, childLocations, onlineVarData, opacity);
    return fillColor as RGBAColor;
  };

  const getLineColor = (item: any) => {
    const locName = item.properties?.name;
    const isHoveredFloor = locName === hoveredFloorId;
    let opacity = 255;
    if (hoveredFloorId && !isHoveredFloor) opacity = defaultOpacity;
    return [0, 0, 0, opacity] as RGBAColor; // default to black
  };

  const elevatedData = addElevationToFloors(floorPlan, defaultElevation);

  return new SolidPolygonLayer({
    id: 'building',
    data: elevatedData.features,
    extruded: true,
    wireframe: true,
    getPolygon: (d: any) => d.geometry.coordinates[0],
    getElevation: 5,
    getFillColor: (d: any) => getFillColor(d),
    getLineColor: (d: any) => getLineColor(d),
    pickable: true,
    onHover: (obj: any) => {
      if (setHoveredFloor) {
        if (obj?.picked) {
          const objData = obj.object;
          const locName: string = objData.properties?.name;
          if (locName) setHoveredFloor({ floorId: locName, hoveredType: FloorHoverType.map });
        } else setHoveredFloor(undefined);
      }
    },
    onClick: (obj: any) => {
      if (obj?.picked) {
        const objData = obj.object;
        const locName: string = objData.properties?.name;
        if (locName && dispatch) {
          dispatch(setCurrentLocation(`${currentLocation}#${locName}`));
          if (setHoveredFloor) setHoveredFloor(undefined);
        }
      }
    },
    parameters: {
      depthMask: !hoveredFloorId, // Hack to make solid layer more visible under transparent
    },
  });
};

export default get3DBuildingLayer;
